import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaEdit, FaLink, FaPlus } from 'react-icons/fa';
import { CustomButton } from '../../../../../components/CustomButton';
import { Input, InputGroup } from 'rsuite';
import SearchIcon from '@rsuite/icons/Search';
import * as S from './styles';
import { CreateEditBorderModal } from './CreateEdit';
import api from '../../../../../services/api';
import { LinkBorderModal } from './Link';

function toTitleCase(text = '') {
  return text
    .toLowerCase()
    .replace(/(^|\s|\()([a-zà-ÿ])/g, (match) => match.toUpperCase());
}

export function Bordas({
  borders,
  handleSetBorders,
  tamanhos,
  idGrupo,
  idEmpresa,
  selecteds,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [currentBorder, setCurrentBorder] = useState({});
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [search, setSearch] = useState('');

  const toggleModal = (value) => {
    setModalOpen((prev) => value ?? !prev);
  };

  const handleOpenBorder = (border) => {
    setCurrentBorder(border);
    toggleModal(true);
  };

  const handleSetCurrentBorder = (border) => {
    setCurrentBorder(border);
  };

  const getBorders = useCallback(async () => {
    const { data } = await api.get(`/produto/listarBordasPorGrupo/${idGrupo}`);

    if (!Array.isArray(data) || !data?.length > 0) {
      console.log('no data');
      return;
    }

    let parsedBordes = [];

    data?.forEach((curr) => {
      const currParsed = {
        id: curr?.produto_tamanho_IdTamanho,
        descricao: tamanhos.find(
          (tam) => tam.Id === curr?.produto_tamanho_IdTamanho,
        )?.Descricao,
        preco: Number(curr?.produto_tamanho_Preco ?? 0),
        precoDesconto: Number(curr?.produto_tamanho_PrecoDesconto ?? 0),
      };

      const exist = parsedBordes.find(
        (parsed) => parsed?.descricao === curr?.Descricao,
      );

      if (exist) {
        parsedBordes = parsedBordes.map((parsed) =>
          parsed?.descricao !== curr?.Descricao
            ? parsed
            : {
                ...parsed,
                tamanhos: currParsed.id
                  ? [...parsed?.tamanhos, currParsed]
                  : [...parsed?.tamanhos],
              },
        );
      }

      if (!exist) {
        parsedBordes.push({
          id: curr.Id,
          descricao: curr.Descricao,
          codigo: curr.CodigoPDV,
          tamanhos: currParsed.id ? [currParsed] : [],
          ...curr,
        });
      }
    });
    handleSetBorders(parsedBordes);
  }, []);

  useEffect(() => {
    if (borders.length === 0 && idGrupo) {
      getBorders();
    }
  }, []);

  const sizeOrder = useMemo(
    () => tamanhos.map((tam) => tam.Id),
    [tamanhos],
  );

  const filteredBorders = useMemo(
    () =>
      borders?.filter((border) =>
        (border?.descricao ?? '')
          .toLowerCase()
          .includes(search.toLowerCase()),
      ) ?? [],
    [borders, search],
  );

  return (
    <S.Container>
      <S.Header>
        <S.HeaderInfo>
          <S.Title>Bordas</S.Title>
          <S.Subtitle>
            {borders.length} {borders.length === 1 ? 'borda cadastrada' : 'bordas cadastradas'}
          </S.Subtitle>
        </S.HeaderInfo>
        <S.HeaderActions>
          <CustomButton
            startIcon={<FaPlus />}
            onClick={() => {
              toggleModal();
              setCurrentBorder({});
            }}
          >
            Cadastrar
          </CustomButton>
          <CustomButton
            startIcon={<FaLink />}
            onClick={() => {
              setLinkModalOpen(true);
              setCurrentBorder({});
            }}
          >
            Vincular
          </CustomButton>
        </S.HeaderActions>
      </S.Header>

      {borders?.length > 0 && (
        <S.SearchWrapper>
          <InputGroup inside style={{ width: '100%' }}>
            <InputGroup.Addon>
              <SearchIcon />
            </InputGroup.Addon>
            <Input
              placeholder="Buscar borda pelo nome..."
              value={search}
              onChange={setSearch}
            />
          </InputGroup>
        </S.SearchWrapper>
      )}

      <S.Content>
        {filteredBorders?.map((border) => {
          const cardSizes = border?.tamanhos
            ?.filter((tam) =>
              selecteds.some(
                (x) => (tam.id || tam.idTamanho) === x.IdTamanho && x.Ativo,
              ),
            )
            .sort(
              (a, b) =>
                sizeOrder.indexOf(a.id || a.idTamanho) -
                sizeOrder.indexOf(b.id || b.idTamanho),
            );

          return (
            <S.BorderCard
              key={border?.descricao}
              onClick={() => handleOpenBorder(border)}
            >
              <S.BorderCardHeader>
                <S.BorderCardTitle>
                  {toTitleCase(border?.descricao)}
                </S.BorderCardTitle>
                <S.EditIcon className="border-edit-icon">
                  <FaEdit size={14} />
                </S.EditIcon>
              </S.BorderCardHeader>
              <S.SizeList>
                {cardSizes?.length > 0 ? (
                  cardSizes.map((tam) => (
                    <S.SizeRow key={tam?.id ?? tam?.descricao}>
                      <S.SizeLabel>{toTitleCase(tam?.descricao)}</S.SizeLabel>
                      <S.SizePrice>
                        {tam?.preco?.toLocaleString('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                        })}
                      </S.SizePrice>
                    </S.SizeRow>
                  ))
                ) : (
                  <S.NoSizesText>Nenhum tamanho vinculado</S.NoSizesText>
                )}
              </S.SizeList>
            </S.BorderCard>
          );
        })}
      </S.Content>

      {borders.length === 0 && (
        <S.BordersEmptyList>
          <S.EmptyText>Não tem bordas cadastradas</S.EmptyText>
        </S.BordersEmptyList>
      )}

      {borders.length > 0 && filteredBorders.length === 0 && (
        <S.BordersEmptyList>
          <S.EmptyText>Nenhuma borda encontrada para "{search}"</S.EmptyText>
        </S.BordersEmptyList>
      )}

      {modalOpen && (
        <CreateEditBorderModal
          modalOpen={modalOpen}
          toggleModal={toggleModal}
          handleSetBorders={handleSetBorders}
          borders={borders}
          tamanhos={tamanhos.filter(
            (x) =>
              x.Ativo &&
              selecteds.some(
                (s) => String(s.IdTamanho) === String(x.Id) && +s.Ativo === 1,
              ),
          )}
          currentBorder={currentBorder}
          handleSetCurrentBorder={handleSetCurrentBorder}
          idGrupo={idGrupo}
          idEmpresa={idEmpresa}
          getBorders={getBorders}
          selecteds={selecteds}
        />
      )}
      {linkModalOpen && (
        <LinkBorderModal
          modalOpen={linkModalOpen}
          toggleModal={setLinkModalOpen}
          idEmpresa={idEmpresa}
          idGrupo={idGrupo}
          tamanhos={tamanhos.filter(
            (x) =>
              x.Ativo &&
              selecteds.some(
                (s) => String(s.IdTamanho) === String(x.Id) && +s.Ativo === 1,
              ),
          )}
          existingBorders={borders}
          onSave={() => getBorders()}
          selecteds={selecteds}
        />
      )}
    </S.Container>
  );
}
